<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';
import GoogleAuthIcon from '@/assets/images/svg/auth-google.svg?component';
import MicrosoftAuthIcon from '@/assets/images/svg/auth-microsoft.svg?component';
import EmailAuthIcon from '@/assets/images/svg/auth-email.svg?component';

type ButtonVariant = 'primary' | 'secondary';
type IconType = 'google' | 'email' | 'microsoft';
type ButtonType = 'button' | 'submit' | 'reset';

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    disabled?: boolean;
    text?: string;
    loadingText?: string;
    fullWidth?: boolean;
    variant?: ButtonVariant;
    iconType?: IconType;
    showIcon?: boolean;
    buttonType?: ButtonType;
  }>(),
  {
    loading: false,
    disabled: false,
    text: 'Sign in with Google',
    loadingText: 'Connecting to Google...',
    fullWidth: false,
    variant: 'secondary',
    iconType: 'google',
    showIcon: true,
    buttonType: 'button',
  }
);
const emit = defineEmits<{
  (e: 'click'): void;
}>();

const isPrimary = computed(() => props.variant === 'primary');

const disabledStyle = computed<CSSProperties | undefined>(() => {
  if (!props.disabled) return undefined;

  if (isPrimary.value) {
    return {
      backgroundColor: '#b2b9c4',
      borderColor: '#b2b9c4',
      color: '#ffffff',
      opacity: '0.6',
      cursor: 'not-allowed',
    };
  }

  return {
    backgroundColor: '#f5f5f5',
    borderColor: '#d6d6d6',
    color: '#9c9c9c',
    opacity: '0.6',
    cursor: 'not-allowed',
  };
});

const spinnerBorderClass = computed(() =>
  isPrimary.value ? 'tw:border-t-white' : 'tw:border-t-[#4285f4]'
);
</script>

<template>
  <button
    class="auth-action-button tw:flex tw:items-center tw:justify-center tw:gap-2 tw:h-10 tw:min-h-10 tw:px-3 tw:rounded-md tw:border tw:text-base tw:transition-colors tw:duration-200 tw:ease-out disabled:tw:cursor-not-allowed disabled:tw:opacity-70 disabled:tw:pointer-events-none"
    :class="{
      'tw:w-full': fullWidth,
      'tw:opacity-70 tw:cursor-not-allowed': loading,
      'auth-action-button--primary tw:bg-[#ff7a08] tw:border-[#ff7a08] tw:text-white tw:font-semibold':
        isPrimary,
      'auth-action-button--secondary tw:bg-white tw:border-[#8c8c8c] tw:text-[#5e5e5e] tw:font-medium':
        !isPrimary,
    }"
    :style="disabledStyle"
    :type="buttonType"
    :disabled="loading || disabled"
    @click="emit('click')"
  >
    <div
      v-if="loading"
      class="tw:flex tw:items-center tw:justify-center tw:w-[18px] tw:h-[18px] tw:shrink-0"
    >
      <div
        :class="[
          'tw:w-4 tw:h-4 tw:border-2 tw:border-[#f3f3f3] tw:rounded-full tw:animate-spin',
          spinnerBorderClass,
        ]"
      ></div>
    </div>
    <GoogleAuthIcon
      v-else-if="showIcon && iconType === 'google'"
      class="tw:shrink-0"
      style="width: 16px; height: 16px"
    />
    <MicrosoftAuthIcon
      v-else-if="showIcon && iconType === 'microsoft'"
      class="tw:shrink-0"
      style="width: 16px; height: 16px"
    />
    <EmailAuthIcon v-else-if="showIcon" class="tw:shrink-0" style="width: 16px; height: 16px" />
    <span class="tw:flex-1 tw:text-center tw:whitespace-nowrap">
      {{ loading ? loadingText : text }}
    </span>
  </button>
</template>

<style scoped>
/* NOTE: hover/focus classes with tw: variants are unreliable here (dynamic + arbitrary values),
   so interaction states are intentionally implemented via scoped CSS. */
.auth-action-button:not(:disabled):focus-visible {
  outline: none;
}

.auth-action-button:not(:disabled) {
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease,
    box-shadow 0.2s ease;
}

.auth-action-button--primary:not(:disabled):hover {
  opacity: 0.8;
}

.auth-action-button--primary:not(:disabled):focus-visible {
  box-shadow: 0 0 0 2px #ffb400;
}

.auth-action-button--secondary:not(:disabled):hover {
  background-color: #f8f9fa;
}

.auth-action-button--secondary:not(:disabled):focus-visible {
  box-shadow: 0 0 0 2px #4285f4;
}
</style>
